<!-- Backdrop -->
<div class="ct-import-backdrop"
     (click)="onClose()"
     (keydown)="onModalKeydown($event)">
</div>

<!-- Modal -->
<div class="ct-import-wrap"
     role="dialog"
     aria-modal="true"
     [attr.aria-label]="'Import Data into ' + (tableTitle || 'table')">
  <div #modalContent class="modal-content"
       (click)="$event.stopPropagation()"
       (keydown)="onModalKeydown($event)">

    <!-- Hidden file input -->
    <input #fileInput class="visually-hidden"
           type="file"
           accept=".csv,.txt,.xlsx,.xls"
           (change)="onFileSelected($event)"
           aria-label="Select file to import" />

    <!-- Screen reader step announcements -->
    <div class="visually-hidden" aria-live="polite" aria-atomic="true">{{ stepAnnouncement }}</div>

    <!-- ─── Header ─── -->
    <div class="modal-header">
      <div class="modal-header-left">
        <h2 class="modal-title" id="import-modal-title">Import Data</h2>
        <p *ngIf="step === 'upload'">Select a file to upload</p>
        <p *ngIf="step === 'mapping'">Map file columns to table fields</p>
        <p *ngIf="step === 'preview'">Review data before importing</p>
      </div>
      <div class="modal-header-right">
        <button #firstFocusable
                type="button"
                class="close"
                (click)="onClose()"
                aria-label="Close import dialog">
          <lucide-angular name="x" [size]="18" aria-hidden="true"></lucide-angular>
        </button>
      </div>
    </div>

    <!-- ─── Body ─── -->
    <div class="modal-body">

      <!-- ═══ Step 1: Upload ═══ -->
      <div class="ct-import-step ct-import-step--upload" *ngIf="step === 'upload'">
        <div class="import-dropzone"
             [class.dragover]="isDragOver"
             [class.has-file]="!!fileName"
             tabindex="0"
             aria-label="Choose a file to upload. Drag and drop or press Enter to browse."
             (dragover)="onDragOver($event)"
             (dragleave)="onDragLeave($event)"
             (drop)="onDrop($event)"
             (click)="openFilePicker()"
             (keydown.enter)="openFilePicker(); $event.preventDefault()"
             (keydown.space)="openFilePicker(); $event.preventDefault()">
          <ng-container *ngIf="!fileName">
            <lucide-angular class="dropzone-icon" name="cloud-upload" [size]="40" [strokeWidth]="1.5" aria-hidden="true"></lucide-angular>
            <span class="dropzone-label">Drag &amp; drop your file here</span>
            <span class="dropzone-sublabel">or click to browse</span>
            <span class="dropzone-hint">Supports CSV, TXT, XLS, XLSX</span>
          </ng-container>
          <ng-container *ngIf="fileName">
            <div class="file-selected-card">
              <div class="file-icon">
                <lucide-angular name="file-text" [size]="18" aria-hidden="true"></lucide-angular>
              </div>
              <div class="file-meta">
                <div class="file-name">{{ fileName }}</div>
                <div class="file-type">{{ fileType === 'xlsx' ? 'Excel Spreadsheet' : 'CSV File' }}</div>
              </div>
            </div>
            <span class="dropzone-sublabel">Click to replace</span>
          </ng-container>
        </div>

        <app-inline-banner *ngIf="importError" type="error">{{ importError }}</app-inline-banner>

        <p class="template-hint">
          Looking for the right format?
          <button type="button"
                  class="template-link"
                  (click)="downloadTemplate(); $event.stopPropagation()"
                  aria-label="Download template">
            Download template
          </button>
        </p>
      </div>

      <!-- ═══ Step 2: Mapping ═══ -->
      <div class="ct-import-step ct-import-step--mapping" *ngIf="step === 'mapping'">
        <app-inline-banner [type]="matchBannerType">
          <ng-container *ngIf="matchBannerType === 'info'">
            Hit <strong>Auto-match</strong> to map your columns automatically, or assign them using the dropdowns below.
          </ng-container>
          <ng-container *ngIf="matchBannerType === 'warn'">
            <strong>{{ mappedFieldCount }} of {{ mappings.length }}</strong> columns matched.
            Assign the remaining ones manually, or skip them &mdash; they won't be imported.
          </ng-container>
          <ng-container *ngIf="matchBannerType === 'success'">
            <strong>{{ mappedFieldCount }} of {{ mappings.length }}</strong> columns matched &mdash; looking good!
            Review the mapping below before continuing.
          </ng-container>
        </app-inline-banner>

        <div class="mapping-toolbar">
          <h5 class="section-label" id="mapping-heading">Field Mapping</h5>
          <button type="button"
                  class="btn-auto-match"
                  (click)="autoMatchFields()"
                  aria-label="Automatically match file columns to table fields">
            <lucide-angular name="sparkles" [size]="14" aria-hidden="true"></lucide-angular>
            Auto-match
          </button>
          <div class="mapping-search">
            <lucide-angular name="search" [size]="14" aria-hidden="true"></lucide-angular>
            <input type="text"
                   placeholder="Filter columns..."
                   [value]="searchTerm"
                   (input)="onSearchChange($any($event.target).value)"
                   aria-label="Filter mapping columns" />
          </div>
        </div>

        <div class="mapping-scroll" aria-labelledby="mapping-heading">
          <div class="mapping-grid-header">
            <span></span>
            <span>File Column</span>
            <span></span>
            <span>Table Field</span>
          </div>
          <div *ngFor="let mapping of filteredMappings; trackBy: trackByHeader"
               class="mapping-grid-row"
               [class.mapping-row--matched]="!!mapping.fieldName"
               [class.mapping-row--required-skip]="isRequiredSkip(mapping)">
            <div class="mapping-check">
              <input type="checkbox"
                     [checked]="!!mapping.fieldName"
                     (change)="onMappingToggle(mapping.csvHeader, $any($event.target).checked)"
                     [attr.aria-label]="'Include column ' + mapping.csvHeader" />
            </div>
            <span class="mapping-col-label">
              {{ mapping.csvHeader }}
              <span *ngIf="isRequiredSkip(mapping)"
                    class="required-badge"
                    aria-label="Required field — currently skipped">Required</span>
            </span>
            <span class="mapping-arrow" aria-hidden="true">&rarr;</span>
            <div class="mapping-select-wrap">
              <select class="mapping-select"
                      (change)="onMappingFieldChange(mapping.csvHeader, $any($event.target).value)"
                      [attr.aria-label]="'Map ' + mapping.csvHeader + ' to table field'">
                <option value="" [selected]="!mapping.fieldName">-- Skip --</option>
                <option *ngFor="let field of fields"
                        [value]="field.fieldName"
                        [selected]="field.fieldName === mapping.fieldName"
                        [attr.disabled]="isFieldUsedElsewhere(mapping.csvHeader, field.fieldName) ? '' : null">
                  {{ field.label }}
                </option>
              </select>
              <svg *ngIf="!!mapping.fieldName"
                   class="mapped-check"
                   viewBox="0 0 20 20"
                   xmlns="http://www.w3.org/2000/svg"
                   aria-label="Mapped">
                <circle class="check-circle" cx="10" cy="10" r="9"/>
                <path class="check-path" d="M6 10l3 3 5-5"/>
              </svg>
            </div>
          </div>
          <div *ngIf="showEmptySearch" class="mapping-empty">
            <div class="mapping-empty-icon" aria-hidden="true">
              <lucide-angular name="search" [size]="22" aria-hidden="true"></lucide-angular>
            </div>
            <h5>No matches</h5>
            <p>Try a different search term.</p>
          </div>
        </div>

        <app-inline-banner *ngIf="importError" type="error">{{ importError }}</app-inline-banner>
      </div>

      <!-- ═══ Step 3: Preview + Validation ═══ -->
      <div class="ct-import-step ct-import-step--preview" *ngIf="step === 'preview'">
        <ng-container *ngIf="validationResult">
          <app-inline-banner [type]="validationResult.invalidRowCount === 0 ? 'success' : 'warn'">
            <ng-container *ngIf="validationResult.invalidRowCount === 0">
              All <strong>{{ validationResult.totalRows }}</strong> rows are valid and ready to import.
            </ng-container>
            <ng-container *ngIf="validationResult.invalidRowCount > 0">
              <strong>{{ validationResult.invalidRowCount }}</strong> of <strong>{{ validationResult.totalRows }}</strong>
              rows have issues across <strong>{{ validationErrorColumns.length }}</strong> column(s).
              Review the errors below or import only the <strong>{{ validationResult.validRowCount }}</strong> valid rows.
            </ng-container>
          </app-inline-banner>
        </ng-container>

        <!-- Error accordion -->
        <section *ngIf="validationResult && validationResult.invalidRowCount > 0"
             class="error-accordion"
             aria-label="Validation errors by column">
          <details *ngFor="let csvHeader of validationErrorColumns"
                   class="error-group">
            <summary (keydown.enter)="$event.preventDefault()">
              {{ columnLabelForHeader(csvHeader) }}
              <span class="error-badge" aria-label="error count">{{ errorsForColumn(csvHeader).length }}</span>
            </summary>
            <ol class="error-list">
              <li *ngFor="let err of errorsForColumn(csvHeader).slice(0, 5)"
                  class="error-item">
                <strong>Row {{ err.rowIndex + 1 }}:</strong>
                {{ err.message }}
                <span *ngIf="err.expected" class="error-expected"> &mdash; Expected: {{ err.expected }}</span>
              </li>
              <li *ngIf="errorsForColumn(csvHeader).length > 5"
                  class="error-more">
                &hellip; and {{ errorsForColumn(csvHeader).length - 5 }} more
              </li>
            </ol>
          </details>
        </section>

        <div class="preview-header">
          <h5 class="section-label">Data Preview</h5>
          <p class="section-hint">Showing up to 5 rows from your file</p>
        </div>

        <div class="preview-wrap">
          <table class="preview-table" aria-label="Import data preview">
            <thead>
              <tr>
                <th *ngFor="let header of csvHeaders"
                    [class.col-skipped]="isColumnSkipped(header)"
                    [attr.aria-label]="isColumnSkipped(header) ? ('Column ' + header + ', ignored — will not be imported') : null">
                  {{ header }}
                  <span *ngIf="isColumnSkipped(header)" class="ignored-badge" aria-hidden="true">Ignored</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of previewData; let ri = index">
                <td *ngFor="let header of csvHeaders"
                    [class.cell-error]="cellHasError(ri, header)"
                    [class.col-skipped]="isColumnSkipped(header)">
                  {{ row[header] }}
                  <svg *ngIf="cellHasError(ri, header)"
                       class="cell-error-icon"
                       viewBox="0 0 20 20"
                       xmlns="http://www.w3.org/2000/svg"
                       aria-hidden="true">
                    <circle class="error-circle" cx="10" cy="10" r="9"/>
                    <path class="error-path" d="M7 7l6 6M13 7l-6 6"/>
                  </svg>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <app-inline-banner *ngIf="importError" type="error">{{ importError }}</app-inline-banner>
      </div>

    </div>

    <!-- ─── Footer ─── -->
    <div class="modal-footer-bottom">
      <div class="modal-footer-bottom-row">

        <!-- Left group: backward navigation -->
        <div class="footer-left">
          <button *ngIf="step === 'mapping'"
                  type="button"
                  class="btn-outline"
                  (click)="goBackToUpload()"
                  aria-label="Go back to file selection">
            <lucide-angular name="chevron-left" [size]="13" [strokeWidth]="2.5" aria-hidden="true"></lucide-angular>
            Back
          </button>
          <button *ngIf="step === 'preview'"
                  type="button"
                  class="btn-outline"
                  (click)="goBackToMapping()"
                  aria-label="Go back to column mapping">
            <lucide-angular name="chevron-left" [size]="13" [strokeWidth]="2.5" aria-hidden="true"></lucide-angular>
            Back
          </button>
        </div>

        <!-- Right group: cancel + forward actions -->
        <div class="footer-right">
          <button type="button"
                  class="btn-outline"
                  (click)="onClose()">Cancel</button>

          <button *ngIf="step === 'upload'"
                  type="button"
                  class="btn btn-common-primary"
                  [attr.aria-disabled]="!fileName ? 'true' : null"
                  (click)="fileName && openFilePicker()"
                  aria-label="Choose File">
            Choose File
          </button>

          <button *ngIf="step === 'mapping'"
                  type="button"
                  class="btn btn-common-primary"
                  [attr.aria-disabled]="!hasMappings ? 'true' : null"
                  (click)="hasMappings && goToPreview()"
                  aria-label="Review &amp; Import">
            Review &amp; Import
            <lucide-angular name="chevron-right" [size]="13" [strokeWidth]="2.5" aria-hidden="true"></lucide-angular>
          </button>

          <ng-container *ngIf="step === 'preview'">
            <button *ngIf="validationResult && validationResult.invalidRowCount > 0"
                    type="button"
                    class="btn btn-common-primary btn--secondary-action"
                    [attr.aria-disabled]="validationResult.validRowCount === 0 ? 'true' : null"
                    (click)="validationResult.validRowCount > 0 && executeImport(true)"
                    [attr.aria-label]="'Import only ' + validationResult.validRowCount + ' valid rows'">
              Import {{ validationResult.validRowCount }} Valid
            </button>
            <button type="button"
                    class="btn btn-common-primary"
                    [attr.aria-disabled]="(!hasMappings || (validationResult && validationResult.invalidRowCount > 0)) ? 'true' : null"
                    (click)="hasMappings && !(validationResult && validationResult.invalidRowCount > 0) && executeImport(false)"
                    [attr.aria-label]="'Import all ' + (validationResult?.totalRows ?? 0) + ' rows'">
              {{ validationResult && validationResult.invalidRowCount > 0 ? 'Import All' : 'Import' }}
            </button>
          </ng-container>
        </div>

      </div>
    </div>
  </div>
</div>
